import { Component, EventEmitter, forwardRef, Input, Output, ViewChild, ChangeDetectorRef } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatTable } from "@angular/material/table";
import { Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "src/app/config";
import { LocalStorage } from "src/app/_helpers";
import { ActivatedRoute, Router } from "@angular/router";
import { DataTableService, QUERY_PARAM_KEYS } from "src/app/_services";

const DEBUG_LOGGING = false; // Set to true to enable debug logs

@Component({
  selector: "data-table-footer",
  templateUrl: "./data-table-footer.component.html",
  styleUrls: ['./data-table-footer.component.scss'],
  providers: [{
    provide: MatTable,
      useExisting: forwardRef(() =>  DataTableFooterComponent)
  }]
})
export class DataTableFooterComponent {

  @ViewChild(MatPaginator, {static: true}) matPaginator: MatPaginator;

  @Output() public paginator = new EventEmitter<{ paginator: MatPaginator; footer: DataTableFooterComponent }>();
  @Output() public zoomChange = new EventEmitter<number>();
  @Output() public loaded = new EventEmitter<boolean>();

  @Output() public onLoad = new EventEmitter();
  @Output() public onSearch = new EventEmitter();

  @Output() public onToggleFullScreen = new EventEmitter();

  @Input() public zoom: number;

  @Input() public localStorageKey: string;
  @Input() public pageSize: any = PAGE_SIZE.DEFAULT;
  @Input() public pageSizeOptions: any = PAGE_SIZE_OPTIONS.DEFAULT;
  @Input() public hidePageSize = false;
  @Input() public isFullScreen = undefined;
  
  // Set by data-table component via datasource subscription, OR can be overridden by parent input for special cases
  @Input() public isLoading = false;
  @Input() public showPagination = true;
  @Input() public isInfiniteScroll = false;
  @Input() public search: Observable<any>;
  
  public dataUrl: string; // Set by data-table component via setDataUrl()

  private _subscriptions = new Subscription();

  private _searchCount = 0;
  private _isInitialized = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _dataTableService: DataTableService<any, any>,
    private _cd: ChangeDetectorRef
  ) {}

  private _log(message: string, ...args: any[]): void {
    if (DEBUG_LOGGING) {
      console.log(`[Footer] ${message}`, ...args);
    }
  }

  private _warn(message: string, ...args: any[]): void {
    if (DEBUG_LOGGING) {
      console.warn(`[Footer] ${message}`, ...args);
    }
  }

  public ngOnInit() {
    // Setup paginator labels
    this.matPaginator._intl.nextPageLabel = '';
    this.matPaginator._intl.previousPageLabel = '';

    if (this.localStorageKey) {
      this.pageSize = LocalStorage.getValue(this.localStorageKey) || PAGE_SIZE[this.localStorageKey] || PAGE_SIZE.DEFAULT;
      
      const pageSizeKey = this.localStorageKey.replace('PAGE_SIZE_', '');
      this.pageSizeOptions = PAGE_SIZE_OPTIONS[pageSizeKey] || this.pageSizeOptions;
    }

    // Emit paginator and footer reference for data-table to hook up
    this.paginator.emit({ paginator: this.matPaginator, footer: this });
  }

  private _hasValidState(state: any): boolean {
    return !!(state && (state.pageIndex > 0 || state.orderBy));
  }

  private _extractQueryParams(queryParams: any): { pageIndex: number; orderBy: string } {
    const queryPage = parseInt(queryParams[QUERY_PARAM_KEYS.PAGE], 10);
    const queryOrderBy = queryParams[QUERY_PARAM_KEYS.ORDER_BY];
    
    return {
      pageIndex: queryPage > 0 ? queryPage : 0,
      orderBy: queryOrderBy || ''
    };
  }

  private _restorePaginatorState(): void {
    if (!this.dataUrl) return;

    let savedState = this._dataTableService.getTableState(this.dataUrl);
    
    if (!this._hasValidState(savedState)) {
      const fromQuery = this._extractQueryParams(this._route.snapshot.queryParams);
      
      if (this._hasValidState(fromQuery)) {
        savedState = fromQuery;
        this._dataTableService.saveTableState(this.dataUrl, fromQuery.pageIndex, fromQuery.orderBy, this.localStorageKey);
      }
    }

    if (savedState?.pageIndex > 0) {
      this.matPaginator.pageIndex = savedState.pageIndex;
    }
  }
  
  ngAfterViewInit() {
    if (this.dataUrl) {
      this._subscriptions.add(
        this._dataTableService.stateReset$
          .pipe(filter(resetKey => resetKey === this.dataUrl))
          .subscribe(() => this._clearQueryParams())
      );
      
      this._subscriptions.add(
        this._dataTableService.stateChanged$
          .pipe(filter(({ key }) => key === this.dataUrl && this._isInitialized))
          .subscribe(({ state }) => this._syncQueryParamsFromState(state))
      );
    }

    // Subscribe to search events (optional input)
    if (this.search) {
      this._subscriptions.add(
        this.search.subscribe(x => {
          const isInitialSearch = this._searchCount++ === 0;
          
          this.onSearch.emit(x);
          
          if (!isInitialSearch && this.dataUrl) {
            this.matPaginator.pageIndex = 0;
            const currentState = this._dataTableService.getTableState(this.dataUrl);
            this._dataTableService.saveTableState(this.dataUrl, 0, currentState?.orderBy || '', this.localStorageKey);
            this.onLoad.emit();
          }
          
          this.loaded.emit(true);
        })
      );
    }

    // Subscribe to paginator page size changes
    if (this.matPaginator) {
      this._subscriptions.add(
        this.matPaginator.page.subscribe(event => {
          if (event.pageSize && this.localStorageKey) {
            PAGE_SIZE[this.localStorageKey] = event.pageSize;
            LocalStorage.set(this.localStorageKey, event.pageSize);
          }
        })
      );
    }

    // Mark as initialized and sync initial query params if state exists
    this._isInitialized = true;
    this._syncQueryParamsIfNeeded();
  }

  private _syncQueryParamsIfNeeded(): void {
    if (!this.dataUrl) return;

    const savedState = this._dataTableService.getTableState(this.dataUrl);
    if (this._hasValidState(savedState)) {
      this._syncQueryParamsFromState(savedState);
    }
  }

  public setDataUrl(dataUrl: string): void {
    if (this.dataUrl && this.dataUrl !== dataUrl) {
      this._warn('[Footer] dataUrl changed from', this.dataUrl, 'to', dataUrl);
    }
    this.dataUrl = dataUrl;
    
    this._restorePaginatorState();
    
    if (this._isInitialized) {
      this._syncQueryParamsIfNeeded();
    }
  }

  public toggleFullScreen(updatedState = !this.isFullScreen) {
    this.isFullScreen = updatedState;
    this.onToggleFullScreen.emit(updatedState);
  }

  private _getCurrentUrlPath(): string {
    const urlTree = this._router.parseUrl(this._router.url);
    const segments = urlTree.root.children['primary']?.segments;
    return segments?.map(s => s.path).join('/') || '';
  }

  private _updateQueryParams(queryParams: Record<string, any>): void {
    const path = this._getCurrentUrlPath();
    this._router.navigate([path ? '/' + path : '/'], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private _clearQueryParams(): void {
    this._updateQueryParams({ 
      [QUERY_PARAM_KEYS.PAGE]: null, 
      [QUERY_PARAM_KEYS.ORDER_BY]: null 
    });
  }

  private _syncQueryParamsFromState(state: { pageIndex: number; orderBy: string }): void {
    const queryParams = {
      [QUERY_PARAM_KEYS.PAGE]: state.pageIndex > 0 ? state.pageIndex : null,
      [QUERY_PARAM_KEYS.ORDER_BY]: state.orderBy || null
    };
    this._log('Syncing query params from state:', { state, queryParams });
    this._updateQueryParams(queryParams);
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

}
